/**
 * The Workspace Instruction Profiles the App ships. Every thread binds `thread`;
 * a Scheduled Job's run binds `schedule`. Feature Workspace knowledge lives in
 * the Skill Bundles their Actions invoke, not in a profile (ADR 0142).
 */
export const PROFILE_IDS = ["thread", "schedule"] as const;

export type ProfileId = (typeof PROFILE_IDS)[number];

/**
 * Every App-owned Skill Bundle. All of them are discoverable in every thread:
 * there is no per-thread skill set any more, so an Action can invoke any of
 * them in any thread.
 */
export const SKILL_BUNDLE_IDS = [
  "afar-research",
  "query",
  "issue-tracker",
  "triage-labels",
  "domain-docs",
  "workflow",
  "preprint",
  "code-review",
  "codebase-design",
  "diagnosing-bugs",
  "domain-modeling",
  "grill-me",
  "grill-with-docs",
  "grilling",
  "handoff",
  "implement",
  "improve-codebase-architecture",
  "prototype",
  "tdd",
  "teach",
  "to-issues",
  "to-prd",
  "brain",
  "ingest",
  "lint",
  "delete",
  "design",
  "create-design",
  "update-design",
  "add-design-template",
  "add-design-example",
  "create-design-sample",
  "rename-design-system",
  "experiment",
  "manuscript",
  "peer-council",
  "powerpoint",
  "website",
  "images",
] as const;

export type SkillBundleId = (typeof SKILL_BUNDLE_IDS)[number];

/**
 * Skills offered in the composer's `$` search. Action skills are left out on
 * purpose: they are reached through their Action, which arranges the prompt
 * they expect. Agents still receive every Skill Bundle.
 */
export const USER_FACING_SKILL_BUNDLE_IDS = [
  "afar-research",
  "triage-labels",
  "workflow",
  "preprint",
  "diagnosing-bugs",
  "grill-me",
  "grill-with-docs",
  "handoff",
  "implement",
  "improve-codebase-architecture",
  "prototype",
  "teach",
  "to-issues",
  "to-prd",
] as const satisfies ReadonlyArray<SkillBundleId>;

export type UserFacingSkillBundleId = (typeof USER_FACING_SKILL_BUNDLE_IDS)[number];

const USER_FACING_SKILL_BUNDLE_ID_SET = new Set<string>(USER_FACING_SKILL_BUNDLE_IDS);

export const isUserFacingSkillBundleId = (id: string): id is UserFacingSkillBundleId =>
  USER_FACING_SKILL_BUNDLE_ID_SET.has(id);
